import { IDuplicateAccountRow, IDuplicateContactRow, IDuplicateGroup, IDuplicateReport } from "../models/account/IDuplicates";
import { IContactMatch } from "../models/Contact";

// The decisions behind the duplicate warning, the Duplicates page and the account unlink, kept out
// of the components so they can be tested on their own.

// The query string for GET /contact/matches, or null when there is not yet a whole email or a
// number long enough to be a mobile - asking on every keystroke would only return noise.
export function contactMatchQuery(
  email: string | null | undefined,
  phone: string | null | undefined,
  excludeContactId?: string
): string | null {
  const trimmedEmail = (email ?? "").trim();
  const trimmedPhone = (phone ?? "").trim();

  const hasEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail);
  const hasPhone = trimmedPhone.replace(/\D/g, "").length >= 10;

  if (!hasEmail && !hasPhone) return null;

  const params = new URLSearchParams();
  if (hasEmail) params.append("email", trimmedEmail);
  if (hasPhone) params.append("phone", trimmedPhone);
  if (excludeContactId) params.append("excludeContactId", excludeContactId);

  return params.toString();
}

// The query string for POST /contact. reuseExisting hands back someone Core already recognises
// instead of creating a second contact for them.
export function contactCreateQuery(createAccount?: boolean, reuseExisting?: boolean): string {
  const params = new URLSearchParams();
  if (createAccount) params.append("createAccount", "true");
  if (reuseExisting) params.append("reuseExisting", "true");

  const query = params.toString();
  return query ? `?${query}` : "";
}

// A new contact needs an email or a phone number: with neither, nothing can recognise them again and
// every lead from them makes another contact.
export function canBeRecognised(
  email: string | null | undefined,
  telephone1: string | null | undefined,
  telephone2?: string | null
): boolean {
  return [email, telephone1, telephone2].some((value) => (value ?? "").trim() !== "");
}

// The report with one person taken out - after a merge, instead of building the whole report again.
// The totals are worked out from the groups, the same way the API works them out.
export function withoutGroup(report: IDuplicateReport, keptContactId: string): IDuplicateReport {
  const groups = report.groups.filter((g) => g.keptContactId !== keptContactId);

  return {
    groups,
    tidyableContacts: groups.reduce((total, g) => total + g.tidyableContacts, 0),
    tidyableAccounts: groups.reduce((total, g) => total + g.tidyableAccounts, 0),
    relinkedContacts: groups.filter((g) => g.relinksKeptContact).length,
    groupsNeedingReview: groups.filter((g) => g.needsReview).length,
  };
}

// "0:07", "2:45" - how long a slow job has been running.
export function elapsed(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

export type MatchReason = "Same email and mobile" | "Same email" | "Same mobile";

export function matchReason(match: Pick<IContactMatch, "matchedOnEmail" | "matchedOnMobile">): MatchReason {
  if (match.matchedOnEmail && match.matchedOnMobile) return "Same email and mobile";
  return match.matchedOnEmail ? "Same email" : "Same mobile";
}

export interface IMergeStep {
  targetId: string;
  sourceIds: string[];
  // Accounts only: whose bank details the kept account ends up with.
  bankDetailsFromAccountId?: string;
}

// A contact on an account the admin is leaving out stays with that account: merging it into the kept
// contact would put it on two accounts at once, which the API refuses.
export function onLeftOutAccount(
  contact: Pick<IDuplicateContactRow, "accountId">,
  keptAccountId: string | null,
  leftOut: string[]
): boolean {
  return !!contact.accountId && contact.accountId !== keptAccountId && leftOut.includes(contact.accountId);
}

// What the merge on the Duplicates page sends: every account and contact into the kept ones, except
// the rows the admin left out. Accounts go first - contacts on two different accounts are refused
// until those accounts are one.
export function mergePlan(
  group: Pick<IDuplicateGroup, "contacts" | "accounts">,
  keptContactId: string,
  keptAccountId: string | null,
  leftOut: string[] = [],
  bankDetailsFromAccountId: string | null = null
): { accounts: IMergeStep | null; contacts: IMergeStep | null } {
  const accountSources = group.accounts
    .filter((a) => a.id !== keptAccountId && !leftOut.includes(a.id))
    .map((a) => a.id);
  const contactSources = group.contacts
    .filter((c) => c.id !== keptContactId && !leftOut.includes(c.id) && !onLeftOutAccount(c, keptAccountId, leftOut))
    .map((c) => c.id);

  const accounts: IMergeStep | null =
    keptAccountId && accountSources.length > 0 ? { targetId: keptAccountId, sourceIds: accountSources } : null;
  if (accounts && bankDetailsFromAccountId) accounts.bankDetailsFromAccountId = bankDetailsFromAccountId;

  return {
    accounts,
    contacts: contactSources.length > 0 ? { targetId: keptContactId, sourceIds: contactSources } : null,
  };
}

export interface IBankChoice {
  accountId: string;
  accountNumber: number;
  bankDetails: string;
}

// The different sets of bank details among the accounts being merged, the kept account's first. Only
// worth asking about when there is more than one: new leads for this person are paid to the set kept.
export function bankChoices(
  group: { accounts: Pick<IDuplicateAccountRow, "id" | "accountNumber" | "bankDetails">[] },
  keptAccountId: string | null,
  leftOut: string[]
): IBankChoice[] {
  const merging = group.accounts
    .filter((a) => a.id === keptAccountId || !leftOut.includes(a.id))
    .sort((a, b) => (a.id === keptAccountId ? -1 : b.id === keptAccountId ? 1 : 0));

  const choices: IBankChoice[] = [];
  for (const a of merging) {
    if (a.bankDetails && !choices.some((c) => c.bankDetails === a.bankDetails)) {
      choices.push({ accountId: a.id, accountNumber: a.accountNumber, bankDetails: a.bankDetails });
    }
  }
  return choices;
}

export type RowOutcome = { kind: "kept" } | { kind: "merged" } | { kind: "tidied" } | { kind: "review"; reason: string | null };

// What happens to one row on the Duplicates page. The tidy's verdicts are for the suggested kept rows;
// once an admin picks different ones, every other row is simply merged in.
export function rowOutcome(
  row: { id: string; tidyable: boolean; reason: string | null },
  keptId: string | null,
  usingSuggestion: boolean
): RowOutcome {
  if (row.id === keptId) return { kind: "kept" };
  if (!usingSuggestion) return { kind: "merged" };
  if (row.tidyable) return { kind: "tidied" };
  return { kind: "review", reason: row.reason };
}

// PUT /contact keeps the account link when accountId is left out. Only an account the user cleared
// themselves - not one that simply did not load - takes the contact off it.
export function shouldRemoveFromAccount(accountCleared: boolean, selectedAccountId: string | null | undefined): boolean {
  return accountCleared && !selectedAccountId;
}
